import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { googleAdsService } from '../services/googleAdsService';
import { Clock, User, CheckCircle, XCircle, AlertCircle, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../../config';

interface LogEntry {
  id: string;
  timestamp: string;
  userId: string;
  userRole: string;
  action: string;
  customerId: string;
  details: any;
  status: 'SUCCESS' | 'ERROR' | 'PENDING';
  requestId?: string;
}

const AuditLog: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [rollingBack, setRollingBack] = useState<string | null>(null);

  useEffect(() => {
    loadAuditLog();
  }, []);

  const loadAuditLog = async () => {
    try {
      setLoading(true);
      const data = await googleAdsService.getAuditLog();
      setLogs(data);
    } catch (error) {
      console.error('Failed to load audit log:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'APPLY_RECOMMENDATIONS':
        return 'Wdrożenie rekomendacji';
      case 'RUN_AUDIT':
        return 'Uruchomienie audytu';
      case 'PAUSE_KEYWORD':
        return 'Wstrzymanie słowa';
      case 'ENABLE_KEYWORD':
        return 'Włączenie słowa';
      case 'UPDATE_BUDGET':
        return 'Zmiana budżetu';
      case 'PAUSE_CAMPAIGN':
        return 'Wstrzymanie kampanii';
      case 'ENABLE_CAMPAIGN':
        return 'Włączenie kampanii';
      case 'UPDATE_KEYWORD_BID':
        return 'Zmiana stawki słowa';
      case 'ADD_NEGATIVE_KEYWORDS':
        return 'Dodanie negatywnych słów';
      case 'ROLLBACK':
        return 'Cofnięcie zmiany';
      case 'AUTO_OPTIMIZATION':
        return 'Automatyczna optymalizacja';
      default:
        return action;
    }
  };

  const isRollbackable = (action: string) => {
    const rollbackableActions = [
      'UPDATE_BUDGET',
      'PAUSE_CAMPAIGN',
      'ENABLE_CAMPAIGN',
      'UPDATE_KEYWORD_BID',
      'PAUSE_KEYWORD',
      'ENABLE_KEYWORD'
    ];
    return rollbackableActions.includes(action);
  };

  const handleRollback = async (logId: string) => {
    if (!confirm('Czy na pewno chcesz cofnąć tę zmianę?')) {
      return;
    }

    try {
      setRollingBack(logId);
      const response = await axios.post(`${API_URL}/api/google-ads/rollback/${logId}`, {
        userId: 'lukasz.sikorra@gmail.com',
        reason: 'Manual rollback from UI'
      });

      if (response.data.success) {
        alert('Zmiana została cofnięta pomyślnie');
        loadAuditLog(); // Odśwież listę
      }
    } catch (error: any) {
      console.error('Rollback error:', error);
      alert(error.response?.data?.error || 'Błąd podczas cofania zmiany');
    } finally {
      setRollingBack(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'ERROR':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'PENDING':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      // Sprawdź czy data jest prawidłowa
      if (!dateString) return 'Brak daty';
      
      const date = new Date(dateString);
      
      // Sprawdź czy data jest prawidłowa
      if (isNaN(date.getTime())) {
        return 'Nieprawidłowa data';
      }
      
      return new Intl.DateTimeFormat('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Błąd formatowania daty:', error);
      return 'Błąd daty';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-gray-500">Ładowanie historii zmian...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold">Historia zmian</h2>
          <p className="text-sm text-gray-600">Wszystkie operacje wykonane w Google Ads</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {logs.map(log => (
              <div key={log.id} className="border-l-4 border-gray-200 pl-4 py-3 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(log.status)}
                      <span className="font-medium">{getActionLabel(log.action)}</span>
                      <Badge variant="outline" className="text-xs">
                        {log.userRole}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3" />
                        <span>{log.userId}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(log.timestamp)}</span>
                      </div>
                    </div>

                    {log.details && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <pre className="text-xs text-gray-700 overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {log.requestId && (
                      <div className="text-xs text-gray-500">
                        ID: {log.requestId}
                      </div>
                    )}
                    
                    {isRollbackable(log.action) && log.status === 'SUCCESS' && log.action !== 'ROLLBACK' && (
                      <button
                        onClick={() => handleRollback(log.id)}
                        disabled={rollingBack === log.id}
                        className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Cofnij tę zmianę"
                      >
                        <RotateCcw className="w-3 h-3" />
                        {rollingBack === log.id ? 'Cofanie...' : 'Cofnij'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
